import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';

import {
  NavBar,
  type IMenuOption,
  type INavBarOptions,
  type IUtilityTrayOptions,
} from '@opengov/components-nav-bar';
import { CheckCircle, Logout } from '@opengov/react-capital-assets';

import { SaveIndicator } from './components/vcm/VcmControls';
import { GlobeIcon } from './components/vcm/themeIcons';
import { useLanguage, type Locale } from './contexts/LanguageContext';
import { useVcm } from './contexts/VcmContext';
import { useAppNavigation } from './hooks/useAppNavigation';
import { actionCatalog } from './lib/vcm/role-matrix';
import './Layout.css';

/** SPA-routing link adapter for the OG NavBar (renders react-router `<Link>`). */
const NavBarLink = React.forwardRef<
  HTMLAnchorElement,
  Omit<React.ComponentProps<typeof Link> & { href: string }, 'to'>
>((props, ref) => {
  const { href, ...rest } = props;
  return <Link ref={ref} to={href} {...rest} />;
});
NavBarLink.displayName = 'NavBarLink';

export function Layout() {
  const { module, role, currentUser, availableActions, availableRoles, setModule, setRole, saveStatus } = useVcm();
  const { locale, setLocale, tv } = useLanguage();
  const { buildPath, navigateToPath } = useAppNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);

  const dashboardPath = buildPath('dashboard');
  const menuOptions: IMenuOption[] = [
    {
      id: 'dashboard',
      label: tv('Dashboard'),
      url: dashboardPath,
      linkComponent: NavBarLink,
      isActive: location.pathname === dashboardPath,
    },
    ...availableActions.map((actionId) => {
      const entry = actionCatalog[actionId];
      const path = buildPath(entry.href.replace(/^\//, ''));
      return {
        id: actionId,
        label: tv(entry.label),
        url: path,
        linkComponent: NavBarLink,
        isActive: location.pathname === path || location.pathname.startsWith(`${path}/`),
      };
    }),
  ];

  const profileMenu = (
    <>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: 13,
          fontWeight: 700,
          bgcolor: 'primary.main',
          color: 'white',
          cursor: 'pointer',
        }}
        onClick={(e) => setProfileAnchor(e.currentTarget)}
      >
        {currentUser.initials}
      </Avatar>
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={() => setProfileAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
      >
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
            {currentUser.name}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Module</Typography>
        </MenuItem>
        <MenuItem
          selected={module === 'dcm'}
          onClick={() => { setModule('dcm'); setProfileAnchor(null); }}
        >
          {module === 'dcm' && <ListItemIcon><CheckCircle /></ListItemIcon>}
          <ListItemText inset={module !== 'dcm'}>DCM</ListItemText>
        </MenuItem>
        <MenuItem
          selected={module === 'casework'}
          onClick={() => { setModule('casework'); setProfileAnchor(null); }}
        >
          {module === 'casework' && <ListItemIcon><CheckCircle /></ListItemIcon>}
          <ListItemText inset={module !== 'casework'}>Case Work</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>Role</Typography>
        </MenuItem>
        {availableRoles.map((r) => (
          <MenuItem
            key={r}
            selected={r === role}
            onClick={() => { setRole(r); setProfileAnchor(null); }}
          >
            {r === role && <ListItemIcon><CheckCircle /></ListItemIcon>}
            <ListItemText inset={r !== role}>{r}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            setProfileAnchor(null);
            navigate('/veterans-case-mgmt/local/login');
          }}
          sx={{ color: (t) => t.palette.error.main }}
        >
          <ListItemIcon sx={{ color: 'inherit', '& svg': { width: 18, height: 18 } }}>
            <Logout />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );

  const utilityTrayOptions: IUtilityTrayOptions = {
    profileSettingsOptions: {
      placeHolderInitials: currentUser.initials,
    },
    customProfileSlot: profileMenu,
    // Language switcher with globe icon
    customUtilityOptions: {
      utilityName: 'Language',
      muiIcon: GlobeIcon,
      menuSections: [
        {
          menuItems: [
            { label: `English ${locale === 'en' ? '(active)' : ''}`, onClick: () => setLocale('en' as Locale) },
            { label: `Español ${locale === 'es' ? '(activo)' : ''}`, onClick: () => setLocale('es' as Locale) },
          ],
        },
      ],
    },
    // Tools (settings gear icon)
    settingsOptions: {
      menuSections: [
        {
          menuItems: [
            { label: 'Manage Resources', url: buildPath('resources/manage') },
            { label: 'Export', url: buildPath('export') },
            { label: 'Documentation', url: 'https://opengov.com', openInNewTab: true },
          ],
        },
      ],
    },
  };

  const navBarOptions: INavBarOptions = {
    appName: 'Veterans Case Management',
    menuOptions,
    utilityTrayOptions,
    openGovLogoOnClick: () => navigateToPath(''),
  };

  return (
    <div className="layout">
      <nav className="nav-bar">
        <NavBar {...navBarOptions} />
      </nav>
      {saveStatus !== 'idle' && (
        <Box sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}`, bgcolor: (t) => t.palette.background.paper, px: 2, py: 0.5 }}>
          <Box sx={{ maxWidth: 1440, mx: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
            <SaveIndicator status={saveStatus} />
          </Box>
        </Box>
      )}

      <main className="main">
        <Box
          className="container"
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : theme.palette.grey[50],
          }}
        >
          <Box sx={{ flex: '1 1 auto' }}>
            <Box sx={{ maxWidth: 1440, mx: 'auto' }}>
              <Outlet />
            </Box>
          </Box>

          <Box
            component="footer"
            sx={{
              py: (t) => t.spacing(2),
              px: (t) => t.spacing(3),
              borderTop: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: (t) => t.palette.background.paper,
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: (t) => t.palette.text.secondary }}>
              Veterans Case Management &middot; Powered by OpenGov
            </Typography>
          </Box>
        </Box>
      </main>
    </div>
  );
}
